import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';
import { getCanonicalUrl } from '../utils/url';

interface FAQItem {
  question: string;
  answer: string;
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: "What exactly is Reiki?",
      answer: "Reiki is a Japanese healing technique based on the principle that a practitioner can channel energy into a patient by means of touch or near-touch, activating the natural healing processes of the patient's body and restoring physical and emotional well-being. The word \"Reiki\" comes from two Japanese words: \"rei,\" meaning universal, and \"ki,\" meaning life energy."
    },
    {
      question: "How does Reiki work?",
      answer: "Reiki practitioners believe they're transferring universal energy (ki or chi) through their palms to the recipient. This energy is thought to flow where needed in the recipient's body, helping to remove energy blocks, balance the body's energy, and facilitate healing. From a scientific perspective, some researchers suggest Reiki may work through influencing the body's electromagnetic field or through the placebo effect."
    },
    {
      question: "What can Reiki help with?",
      answer: "People seek Reiki for various reasons, including stress reduction, relaxation, pain management, anxiety relief, depression management, and to complement conventional medical treatments. Some use it during pregnancy or to help with sleep problems. While not a replacement for medical care, many find it beneficial as a complementary practice."
    },
    {
      question: "What happens during a Reiki session?",
      answer: "During a typical session, you'll lie fully clothed on a treatment table while the practitioner places their hands lightly on or just above your body in specific energy locations. Each hand position is held for about 3-10 minutes, depending on the area's needs. A complete session usually lasts 60-90 minutes. Many people report feeling sensations like heat, tingling, or deep relaxation."
    },
    {
      question: "Is Reiki religious or spiritual?",
      answer: "While Reiki involves spiritual elements, it's not affiliated with any particular religion or religious practice. People of various faiths practice and receive Reiki. It's based on the concept of universal life energy rather than any specific religious doctrine. Some practitioners do incorporate their own spiritual beliefs into their practice."
    },
    {
      question: "How do you become a Reiki practitioner?",
      answer: "Becoming a Reiki practitioner involves a process called \"attunement,\" where a Reiki Master transfers the ability to channel Reiki energy to a student. Training typically involves three levels (or degrees): First Degree (basic healing), Second Degree (distance healing), and Master/Teacher level. Each level requires specific training and practice."
    },
    {
      question: "Is there scientific evidence supporting Reiki?",
      answer: "Scientific evidence for Reiki is mixed. Some studies suggest benefits for pain, anxiety, and depression, while others show no effect beyond placebo. Research challenges include standardizing Reiki treatments and measuring subtle energy changes. Major medical organizations generally classify Reiki as a complementary approach that may be used alongside conventional treatments but not as a replacement."
    },
    {
      question: "Are there any risks or side effects of Reiki?",
      answer: "Reiki is generally considered safe with minimal risk of adverse effects. Some people report a \"healing crisis\" - temporary worsening of symptoms before improvement - which practitioners view as part of the healing process. The main risk might be delaying necessary medical treatment if Reiki is used exclusively for serious conditions instead of as a complement to conventional care."
    },
    {
      question: "How many Reiki sessions are needed to see benefits?",
      answer: "The number of sessions varies greatly depending on individual circumstances and goals. Some people report feeling benefits after just one session, particularly for stress reduction. For chronic conditions or deeper healing, practitioners often recommend at least 4-6 sessions. Some people choose to have regular sessions as part of their wellness routine."
    },
    {
      question: "Can Reiki be done remotely or at a distance?",
      answer: "Yes, practitioners trained in Second Degree Reiki and beyond can perform \"distance healing.\" They believe Reiki energy transcends physical location and can be directed to someone anywhere in the world. The practitioner typically uses specific symbols and visualization techniques to establish a connection with the recipient. Many recipients report similar sensations and benefits from distance sessions as they do from in-person treatments."
    }
  ];

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <SEO 
        title="Frequently Asked Questions About Reiki | Reiki Near Me"
        description="Find answers to common questions about Reiki healing, what to expect during sessions, and how to choose a practitioner."
        canonicalUrl={getCanonicalUrl('/faq')}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8">
          Frequently Asked Questions About Reiki
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white dark:bg-dark-secondary rounded-lg shadow-sm overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => toggleQuestion(index)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 dark:hover:bg-dark-accent transition-colors duration-200"
              >
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {faq.question}
                </h2>
                {openIndex === index ? (
                  <ChevronUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                )}
              </button>
              <div 
                className={`px-6 overflow-hidden transition-all duration-200 ${
                  openIndex === index ? 'py-4' : 'max-h-0'
                }`}
              >
                <p className="text-gray-600 dark:text-gray-300 prose dark:prose-invert">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}